/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { Injectable, OnDestroy } from '@angular/core';
import { FeatureToggleService, VisibilityService } from '@nx-fe/services';
import { Subject, Subscription } from 'rxjs';
import { IDeviceViewHandler, Point } from '../../remote-control-ng';
import { RemoteControlAnnotationsService } from './annotations.service';
import { rgb2hex } from './color';
import { NxtRemoteControlPositioningService, Rect, Transformation } from './nxt-remote-control-positioning.service';
import { NxtRemoteControlSkinService, ButtonInfo, Skin } from './nxt-remote-control-skin.service';
import { NgRemoteControlConnection, DeviceInfo } from './remote-control.connection';
import { RemoteControlService } from './remote-control.service';
import { skipWhile } from 'rxjs/operators';
import { MouseEventService } from '../../remote-control-ng/services/mouseEventService';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { ArrayHelper } from 'app/modules/remote-control-ng/utils/arrayHelper';
import { ThAckMessage } from 'app/modules/remote-control-ng/messages/pocket-com';
import { RemoteControlStreamingContext } from 'app/modules/remote-control-ng/context/deviceContext';

//declare var ResizeObserver;
interface RemoteControlScreenSession {
  deviceId: string;
  skin: Skin;
  canvas: HTMLCanvasElement;
  inputCanvas: HTMLCanvasElement;
  // video: HTMLVideoElement;
  context: RemoteControlStreamingContext;
}

@Injectable({
  providedIn: 'root',
})
export class NxtRemoteControlScreenService implements IDeviceViewHandler, OnDestroy {
  private readonly SCALE_LIMIT = 1; // TODO - make it smaller than 100% as some encoders can't encode in large scale?
  private readonly MIN_SCALE_FACTOR = 0.6;
  private readonly MAX_SCALE_FACTOR = 1;
  private readonly MIN_QUALITY = 50;
  private readonly MIME_CODEC = 'video/mp4; codecs="avc1.42801f"';

  private remoteControlConnection: NgRemoteControlConnection;
  private rcScreenSession: RemoteControlScreenSession;
  private autoZoomEnabled = true;
  private offsetX = 0;
  private offsetY = 0;

  private sourceBuffer: SourceBuffer;
  private keyFrames = [];
  private queue: Uint8Array;
  private lastKeyFrame: number;
  private debugLogging = false;
  private startTime: number;

  // RV sessions start only after an on device acknowledgment, thus potentially requiring a 2nd PC_RESTART
  // message for proper video scaling. This happens with the initial scale 0, when the skin is configured
  // but still not release (i.e. skin === null). rvNeedsResize triggers that 2nd restart after the streaming starts
  private rvNeedsResize: boolean = false;

  private onUpdateSubject = new Subject<void>();
  public onUpdate$ = this.onUpdateSubject.asObservable();

  private onAnnotationEndSubscription: Subscription;

  private _pageVisibleSubscription$: Subscription;

  constructor(
    private remoteControlService: RemoteControlService,
    private remoteControlSkinService: NxtRemoteControlSkinService,
    private remoteControlPositioningService: NxtRemoteControlPositioningService,
    private featureToggleService: FeatureToggleService,
    private annotationService: RemoteControlAnnotationsService,
    private visibilityService: VisibilityService,
  ) {}

  ngOnDestroy(): void {
    this.unsubscribeToPageVisibile();
  }

  private unsubscribeToPageVisibile(): void {
    if (this._pageVisibleSubscription$) {
      this._pageVisibleSubscription$.unsubscribe();
      this._pageVisibleSubscription$ = undefined;
    }
  }

  public setup = (
    deviceId: string,
    canvas: HTMLCanvasElement,
    inputCanvas: HTMLCanvasElement,
    video: HTMLVideoElement,
    isPopout: boolean,
    setupHtmlElements: boolean
  ): void => {
    const context = this.remoteControlService.getRemoteControlContext(deviceId);
    // in case of popout, try to copy the loaded skin and settings
    const oldView = context?.deviceViewHandler as NxtRemoteControlScreenService;
    let skin = undefined;
    if (oldView) {
      if (isPopout && oldView.rcScreenSession?.deviceId === deviceId) {
        skin = oldView.rcScreenSession.skin;
        this.autoZoomEnabled = oldView.autoZoomEnabled;
      } else {
        this.autoZoomEnabled = true;
      }

      oldView.stopVideoPlayer(); // stop playing video on old view
    }

    context.deviceViewHandler = this;
    this.remoteControlConnection = context.remoteControlConnection as NgRemoteControlConnection;
    this.debugLogging = this.featureToggleService.isEnabled('DeveloperMode');
    this.startTime = Date.now();
    this.lastKeyFrame = -1;
    context.video = video;

    this.rcScreenSession = {
      deviceId,
      skin,
      canvas,
      inputCanvas,
      context,
    };

    this.onAnnotationEndSubscription?.unsubscribe();
    this.onAnnotationEndSubscription = this.annotationService.onAnnotationEnd$.subscribe(_ => {
      this.clearInputCanvas();
    });

    // neutral state, not sure if the 2nd restart message is needed
    this.rvNeedsResize = null;

    if (setupHtmlElements) {
      // comment out, will be called in processScreenMessage() when the first key frame is received
      //this.rcScreenSession.context.mediaSource = this.buildMediaSource();
      //video.src = URL.createObjectURL(this.rcScreenSession.context.mediaSource);
      this.configureVideoPlayer(video, canvas);

      // after the video player loads a new video (loadedmetadata) it changes videoWidth/videoHeight
      video.onresize = () => this.updateRcScreenConfiguration();
      canvas.onresize = () => this.updateRcScreenConfiguration();
      inputCanvas.onresize = () => this.updateRcScreenConfiguration();
      //const resizeObserver = new ResizeObserver(() => this.updateRcScreenConfiguration());
      //resizeObserver.observe(canvas);
    }
  };

  public isAllowedSendMouseEvent = (event: any) => this.isAllowedUserInteraction();

  public isWithinScreen(x: number, y: number): boolean {
    const deviceCoordinates = this.convertToDeviceCoordinates(x, y);
    return deviceCoordinates.x >= 0 && deviceCoordinates.y >= 0;
  }

  public updateRcScreenConfiguration = (forceApply?: boolean): void => {
    // as onresize can be triggered during animation (transition: all 250ms ease-in-out), use window size - canvas size > 200 to determine if animation is in progress
    // TODO - find a better solution, like https://stackoverflow.com/questions/38995209/prevent-triggering-windows-resize-event-when-css-property-is-changed-using-jquer
    if (
      !this.rcScreenSession ||
      !this.rcScreenSession.canvas.clientHeight ||
      window.innerHeight - this.rcScreenSession.canvas.clientHeight > 200
    ) {
      return;
    }

    console.log(
      `Resize: canvas: ${this.rcScreenSession.canvas.clientWidth} x ${this.rcScreenSession.canvas.clientHeight}, video: ${this.rcScreenSession.context.video.clientWidth} x ${this.rcScreenSession.context.video.clientHeight}`
    );
    console.log(
      `Resize: video size: ${this.rcScreenSession.context.video.videoWidth} x ${this.rcScreenSession.context.video.videoHeight}`
    );

    // if video is not playing, force agent to send a full screen
    if (!this.rcScreenSession.context.mediaSource) {
      forceApply = true;
    }

    const deviceScreen = this.getCurrentDeviceScreenSize(this.remoteControlConnection.getScale());
    let offset: Point = {
      x: this.offsetX,
      y: this.offsetY,
    };
    if (this.rcScreenSession.skin) {
      //const deviceScreen: Rect = {
      //    x: 0, y: 0,
      //    width: this.rcScreenSession.context.video.videoWidth,
      //    height: this.rcScreenSession.context.video.videoHeight,
      //};
      const skinRendered = this.remoteControlSkinService.renderSkinIfNeeded(
        this.rcScreenSession.skin,
        this.rcScreenSession.context.rotation,
        this.rcScreenSession.canvas,
        deviceScreen,
        this.autoScaleEnabled(),
        offset
      );

      // Will only send PC_RESTART message here once the video is ready
      if (skinRendered) {
        if (this.autoScaleEnabled() && this.rcScreenSession.context.videoPlayerReady) {
          this.remoteControlConnection.applyChange(
            forceApply,
            Math.floor(this.remoteControlSkinService.getSkinScale(this.rcScreenSession.skin) * 100)
          );
        } else {
          // this.offsetViewIfNeeded(offset.x - this.offsetX, offset.y - this.offsetY);
          this.offsetX = offset.x;
          this.offsetY = offset.y;
        }

        // We need to reposition the video so it matches the screen within the skin
        const screen = this.remoteControlSkinService.renderCoordinates.screen;
        this.positionVideo(screen.x, screen.y, screen.width, screen.height);
        this.rcScreenSession.context.scale =
          screen.width /
          (this.rcScreenSession.context.rotation % 2 === 0
            ? this.rcScreenSession.context.screenWidth
            : this.rcScreenSession.context.screenHeight);
      }
    } else if (this.rcScreenSession.canvas.clientHeight && this.rcScreenSession.context.screenHeight) {
      // && this.rcScreenSession.context.video.videoHeight

      // let width = this.rcScreenSession.context.video.videoWidth;
      // let height = this.rcScreenSession.context.video.videoHeight;
      let width: number;
      let height: number;

      if (this.autoScaleEnabled() && this.rcScreenSession.context.videoPlayerReady) {
        const rect = this.getCurrentDeviceScreenSize();
        const scale = this.calculateScaleWithNoSkin(rect.width, rect.height);
        this.rcScreenSession.context.scale = scale;
        this.remoteControlConnection.applyChange(forceApply, Math.floor(scale * 100));

        // before new full screen arrives, or if the new scale is never sent to device, stretch video to fit
        width = rect.width * scale;
        height = rect.height * scale;
      } else {
        const scale = this.remoteControlConnection.getScale();
        const transformation = {
          previousScale: this.rcScreenSession.context.scale,
          nextScale: scale / 100,
        } as Transformation;
        if (
          this.remoteControlPositioningService.scaleOffset(
            transformation,
            this.rcScreenSession.canvas,
            deviceScreen,
            offset
          )
        ) {
          this.offsetX = offset.x;
          this.offsetY = offset.y;
        }
        this.rcScreenSession.context.scale = scale / 100;
        const rect = this.getCurrentDeviceScreenSize(scale);
        width = rect.width;
        height = rect.height;
      }

      // position video to center
      this.positionVideo(
        (this.rcScreenSession.canvas.clientWidth - width) / 2,
        (this.rcScreenSession.canvas.clientHeight - height) / 2,
        width,
        height
      );
    }
  };

  public prepare = (): void => {
    //this.autoZoomEnabled = true;  // or keep it as is?
    this.remoteControlConnection = this.rcScreenSession.context.remoteControlConnection as NgRemoteControlConnection;
    this.remoteControlConnection.onStatusUpdate = this.onConnectionStatusUpdate;

    // initialize DeviceInput (to handle mouse input on device screen)

    // RemoteControlContext is used for old JS calls, IRemoteControlStreamingContext
    // which extends RemoteControlContext for new RC
    RcContext.instance.initVideoStreamingContext(this.rcScreenSession.context); // or re-get from this.remoteControlService?
    RcContext.instance.deviceInput.setSkinController(this);
  };

  public cleanup = (): void => {
    this.onAnnotationEndSubscription?.unsubscribe();
    this.onAnnotationEndSubscription = null;
    this.clearInputCanvas();
    this.stopVideoPlayer();
    this.offsetX = this.offsetY = 0;
    const deviceInput = RcContext.instance.deviceInput;
    if (deviceInput) {
      deviceInput.unregisterKeyInputListeners();
      deviceInput.unregisterMouseInputListeners(document.getElementById('rcInputCanvas'));
    }
  };

  public scale = (scale: number, forceApply?: boolean): void => {
    // console.log(`autoZoomEnabled: ${!scale}, scale ${scale}`);
    this.autoZoomEnabled = !scale;

    // scale 0 auto zooms and places the scaled down device in the centre of the panel
    if (this.autoZoomEnabled) {
      this.offsetX = this.offsetY = 0;
    }

    // if video is not playing, force agent to send a full screen
    if (!this.rcScreenSession.context.mediaSource) {
      forceApply = true;
    }

    if (scale > 0) {
      this.remoteControlConnection.applyChange(forceApply === true, scale);
    }

    this.updateRcScreenConfiguration(forceApply);
  };

  public autoScaleEnabled = (): boolean => this.autoZoomEnabled;

  public getViewScale = (increment?: number): number =>
    //const rect = this.getCurrentDeviceScreenSize();
    //return this.rcScreenSession.context.video.width / rect.width * 100;
    //return this.rcScreenSession.context.scale * 100;
    this.remoteControlConnection.getScale(increment);

  public fullyVisible = (): boolean => {
    const parentContainer = this.rcScreenSession.context.video.parentElement;
    if (!parentContainer) {
      return false;
    }

    let childWidth = this.rcScreenSession.context.video.width;
    let childHeight = this.rcScreenSession.context.video.height;
    let skinDeviceDrawnRect = this.rcScreenSession.skin?.transformed?.drawnDeviceRect;
    if (skinDeviceDrawnRect) {
      childWidth = skinDeviceDrawnRect.width;
      childHeight = skinDeviceDrawnRect.height;
    }

    return childWidth <= parentContainer.clientWidth && childHeight <= parentContainer.clientHeight;
  };

  public offsetView = (dx: number, dy: number): void => {
    let update = false;
    if (!dx && !dy) {
      this.offsetX = 0;
      this.offsetY = 0;
      update = true;
    } else {
      update = this.offsetViewIfNeeded(dx, dy);
    }

    if (update) {
      this.updateRcScreenConfiguration();
    }
  };

  public convertToDeviceCoordinates = (x: number, y: number): Point => {
    const video = this.rcScreenSession.context.video;
    const deviceView = {
      left: video.offsetLeft - this.offsetX,
      top: video.offsetTop - this.offsetY,
      width: video.width,
      height: video.height,
    };
    x -= this.offsetX;
    y -= this.offsetY;
    let inside =
      x >= deviceView.left &&
      x < deviceView.left + deviceView.width &&
      y >= deviceView.top &&
      y < deviceView.top + deviceView.height;

    if (
      inside &&
      this.rcScreenSession.skin &&
      this.rcScreenSession.skin.original.information.isIrregular &&
      this.rcScreenSession.skin.transformed.canvas
    ) {
      // adjust to x,y inside the transformed skin image
      const px = x - this.remoteControlSkinService.renderCoordinates.skin.x;
      const py = y - this.remoteControlSkinService.renderCoordinates.skin.y;
      const context = this.rcScreenSession.skin.transformed.canvas.getContext('2d');
      const pixel = context.getImageData(px, py, 1, 1).data;
      inside = pixel[3] < 255; // non screen area pixels within skin are fully opaque including notches/cameras
    }
    let deviceX = -1;
    let deviceY = -1;
    if (inside) {
      // convert to device screen position (as ratio)
      deviceX = x - deviceView.left;
      deviceY = y - deviceView.top;
      deviceX /= deviceView.width;
      deviceY /= deviceView.height;
    }
    // console.log('OUTSIDE COORDINATES:', x, y);
    // console.log('DEVICE COORDINATES:', deviceX, deviceY);
    return { x: deviceX, y: deviceY };
  };

  public setCanvasCursor(cursor: string): void {
    this.rcScreenSession.inputCanvas.style.cursor = cursor;
  }

  public takeScreenshot = (includeSkin: boolean): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    const screen: Rect = {
      x: 0,
      y: 0,
      width: this.rcScreenSession.context.video.width,
      height: this.rcScreenSession.context.video.height,
    };

    if (includeSkin && this.rcScreenSession.skin) {
      canvas.width = this.remoteControlSkinService.renderCoordinates.skin.width;
      canvas.height = this.remoteControlSkinService.renderCoordinates.skin.height;
      screen.x = this.rcScreenSession.skin.transformed.information.screen.left;
      screen.y = this.rcScreenSession.skin.transformed.information.screen.top;
      //screen.width = this.remoteControlSkinService.renderCoordinates.screen.width;
      //screen.height = this.remoteControlSkinService.renderCoordinates.screen.height;
    } else {
      canvas.width = this.rcScreenSession.context.video.width;
      canvas.height = this.rcScreenSession.context.video.height;
    }

    const snapshotCanvasContext = canvas.getContext('2d');
    snapshotCanvasContext.fillStyle = 'white';
    snapshotCanvasContext.fillRect(0, 0, canvas.width, canvas.height);
    if (includeSkin && this.rcScreenSession.skin) {
      // draw the skin
      snapshotCanvasContext.drawImage(this.rcScreenSession.skin.transformed.canvas, 0, 0, canvas.width, canvas.height);
    }

    // draw the screen
    snapshotCanvasContext.drawImage(this.rcScreenSession.context.video, screen.x, screen.y, screen.width, screen.height);
    snapshotCanvasContext.restore();
    return canvas;
  };

  private clearInputCanvas(): void {
    const inputCanvas = this.rcScreenSession.inputCanvas;
    inputCanvas?.getContext('2d')?.clearRect(0, 0, inputCanvas.width, inputCanvas.height);
  }

  public onHoverAnnotation(event: any): void {
    const inputCanvas = this.rcScreenSession.inputCanvas;
    const canvas = inputCanvas?.getContext('2d');
    if (!canvas || !this.isAnnotating()) {
      return;
    }

    let x = event.offsetX || event.pageX - event.currentTarget.offsetLeft;
    let y = event.offsetY || event.pageY - event.currentTarget.offsetTop;
    if (!this.isWithinScreen(x, y)) {
      this.clearInputCanvas();
      return;
    }

    const context = this.rcScreenSession.context;
    const annotationState = this.annotationService.currentState;
    const scale = context.scale;
    canvas.clearRect(0, 0, inputCanvas.width, inputCanvas.height);
    switch (annotationState.annotationTool) {
      case 'pen':
        canvas.fillStyle = rgb2hex(annotationState.strokeColor) || '#000000';
        canvas.beginPath();
        canvas.arc(x, y, ((annotationState.strokeWidth || 10) / 2) * scale, 0, 2 * Math.PI, true);
        canvas.fill();
        break;
      case 'eraser':
        canvas.fillStyle = 'rgba(0, 0, 0, 0)';
        canvas.strokeStyle = 'rgb(0, 0, 0)';
        canvas.lineWidth = 2.0;
        canvas.beginPath();
        canvas.arc(x, y, ((annotationState.strokeWidth || 10) / 2) * scale, 0, 2 * Math.PI, true);
        canvas.stroke();
        break;
    }
  }

  // compatibility --- callback from mcrc.com.DeviceInput, registered by setSkinController(this)
  public onClick = (callback: (buttonInfo: ButtonInfo) => void, event: MouseEvent): void => {
    const button = this.findButtonByMousePoint(event);
    if (button) {
      callback(button);
    }
  };

  // compatibility --- callback from mcrc.com.DeviceInput, registered by setSkinController(this)
  // see mcrc-com-DeviceSkinController.onHover(), and mcrc-com-DeviceCanvas.draw.overlay()
  public onHover = (event: any): void => {
    const context = this.rcScreenSession.context;
    const security = RcContext.instance.security;
    let cursor = security.RemoteControlInput ? '' : 'not-allowed';
    const x = event.offsetX || event.pageX - event.currentTarget.offsetLeft;
    const y = event.offsetY || event.pageY - event.currentTarget.offsetTop;
    const isWithin = this.isWithinScreen(x, y);
    if (isWithin) {
      const disableCursorInAnnotations = this.isAnnotating();
      if (disableCursorInAnnotations) {
        cursor = 'none';
      } else if (context.disableMouseInput) {
        cursor = 'not-allowed';
      }
    } else if (this.findButtonByMousePoint(event)) {
      cursor = !security.RemoteControlInput || context.disableMouseInput ? 'disable' : 'pointer';
    }

    this.setCanvasCursor(cursor);
    this.onHoverAnnotation(event);
  };

  private findButtonByMousePoint = (event: any): ButtonInfo => {
    if (!this.rcScreenSession.skin || !this.rcScreenSession.skin.transformed.information.buttons) {
      return null;
    }

    MouseEventService.fixEvent(event);
    let x = event.offsetX || event.pageX - event.currentTarget.offsetLeft;
    let y = event.offsetY || event.pageY - event.currentTarget.offsetTop;
    x -= this.remoteControlSkinService.renderCoordinates.skin.x + this.offsetX;
    y -= this.remoteControlSkinService.renderCoordinates.skin.y + this.offsetY;

    for (const button of this.rcScreenSession.skin.transformed.information.buttons) {
      if (x >= button.area.left && x < button.area.right && y >= button.area.top && y < button.area.bottom) {
        return button;
      }
    }

    return null;
  };

  private offsetViewIfNeeded = (x: number, y: number): boolean => {
    // Limit offset at a time
    const maxLimit = 60;
    const minLimit = -60;
    const limitAroundScreen = 80;

    let targetX: number;
    if (x < maxLimit && x < minLimit) {
      targetX = minLimit;
    } else {
      targetX = x;
    }

    let targetY: number;
    if (y < maxLimit && y < minLimit) {
      targetY = minLimit;
    } else {
      targetY = y;
    }

    // Check if direction is to right or left or top or bottom
    const curr = this.getRelativeClientRect();
    const xDir = targetX >= 0 ? curr.left : curr.right;
    const yDir = targetY >= 0 ? curr.top : curr.bottom;

    const updateX = targetX + xDir <= limitAroundScreen;
    const updateY = targetY + yDir <= limitAroundScreen;
    if (updateX) {
      this.offsetX += targetX;
    }

    if (updateY) {
      this.offsetY += targetY;
    }

    return updateX || updateY;
  };

  private getCurrentDeviceScreenSize = (scale?: number): Rect => {
    const defaultRotation = this.rcScreenSession.context.rotation % 2 === 0;
    let width = defaultRotation ? this.rcScreenSession.context.screenWidth : this.rcScreenSession.context.screenHeight;
    let height = defaultRotation ? this.rcScreenSession.context.screenHeight : this.rcScreenSession.context.screenWidth;
    if (scale > 0) {
      width = Math.floor((width * scale) / 100);
      height = Math.floor((height * scale) / 100);
    }

    return {
      x: 0,
      y: 0,
      width,
      height,
    };
  };

  private calculateScaleWithNoSkin = (width: number, height: number): number => {
    const scaleX = this.rcScreenSession.canvas.clientWidth / width;
    const scaleY = this.rcScreenSession.canvas.clientHeight / height;
    return Math.min(Math.min(scaleX, scaleY), this.SCALE_LIMIT);
  };

  // called by NgRemoteControlConnection when web socket connection status changes
  private onConnectionStatusUpdate = (deviceInfo: DeviceInfo): void => {
    if (deviceInfo.status === DeviceInfo.CONNECTING && deviceInfo.skinName) {
      // load skin can throw and error if the skin is used, but syill not released
      this.setupSkin(deviceInfo.skinName);
    } else if (deviceInfo.status === DeviceInfo.HANDSHAKED) {
      // if MS API doesn't return a skin name, should call https://skinsapi.soti.net/skins.ini and find skin by manufacturer/model returned from initResponse.
      // MS returns null for disabled, Empty string for auto and proper skin name for specific skin
      if (deviceInfo.skinName === '' && deviceInfo.modelName) {
        // try loading skin with model name
        this.setupSkin(deviceInfo.modelName);
      }

      if (!this.rcScreenSession.skin) {
        this.remoteControlSkinService.resetSkins();
      }
      
      // when starting RC with scale as 0, agent is paused and waiting for a PC_RESTART message
      if (!this.remoteControlConnection.getScale()) {
        this.autoZoomEnabled = true;
        this.updateRcScreenConfiguration();
      }
    }
  };

  private setupSkin(skinName: string) {
    this.remoteControlSkinService.loadSkin(skinName).subscribe(
      skin => {
        this.rcScreenSession.skin = skin;
        this.updateRcScreenConfiguration();
      },
      error => {
        console.error(error);
        this.rvNeedsResize = true;
      }
    );
  }

  private positionVideo = (x: number, y: number, width?: number, height?: number): void => {
    this.info('positionVideo(' + x + ', ' + y + ', ' + width + ', ' + height + ')');
    x += this.offsetX;
    y += this.offsetY;
    this.rcScreenSession.context.video.style.left = x + 'px';
    this.rcScreenSession.context.video.style.top = y + 'px';

    if (width) {
      this.rcScreenSession.context.video.width = width;
      this.rcScreenSession.context.video.style.width = width + 'px';
    }
    if (height) {
      this.rcScreenSession.context.video.height = height;
      this.rcScreenSession.context.video.style.height = height + 'px';
    }

    this.onUpdateSubject.next();
  };

  private getRelativeClientRect() {
    const rect = this.rcScreenSession.context.video.getBoundingClientRect();
    const parentRect = this.rcScreenSession.context.video.offsetParent.getBoundingClientRect();
    return {
      bottom: parentRect.bottom - rect.bottom,
      height: rect.height,
      left: rect.left - parentRect.left,
      right: parentRect.right - rect.right,
      top: rect.top - parentRect.top,
      width: rect.width,
    };
  }

  public buildMediaSource = (): MediaSource => {
    if (!('MediaSource' in window) || !MediaSource.isTypeSupported(this.MIME_CODEC)) {
      throw new Error('MediaSource not supported');
    }

    const mediaSource = new MediaSource();
    mediaSource.addEventListener('sourceopen', this.onMediaSourceOpened);
    mediaSource.addEventListener('sourceclose', this.onMediaSourceClosed);

    return mediaSource;
  };

  public stopVideoPlayer = (): void => {
    this.rcScreenSession.context.videoPlayerReady = false;
    this.keyFrames = [];
    this.lastKeyFrame = -1;
    this.queue = null;
    this.rcScreenSession.context.video.load(); // close the existing media source
    this.rcScreenSession.context.mediaSource = null;
    this.sourceBuffer = null;
  };

  private configureVideoPlayer = (video: HTMLVideoElement, canvas: HTMLCanvasElement): void => {
    //video.autoplay = true;
    //(<any>video).playsInline = true;
    //video.muted = true;

    video.addEventListener('loadeddata', () => {
      this.debug('video: loadeddata');
      //if (this.rcScreenSession.context.video.paused) { // when tab is inactive, the video player might be paused
      //    this.rcScreenSession.context.video.play();
      //}
    });

    video.addEventListener('playing', () => {
      this.debug('video: playing');
      // this.renderVideoOnCanvas(this.rcScreenSession.context.video, this.rcScreenSession.skin, this.rcScreenSession.canvas);
    });

    if (this.debugLogging) {
      video.addEventListener('pause', () => {
        this.debug('video: pause');
      });
      video.addEventListener('play', () => {
        this.debug('video: play');
      });

      video.addEventListener('error', () => {
        this.debug('video: error');
      });
      video.addEventListener('suspend', () => {
        this.debug('video: suspend');
      });
      video.addEventListener('waiting', () => {
        this.debug('video: waiting, currentTime: ' + video.currentTime);
      });
      video.addEventListener('loadedmetadata', () => {
        this.debug('video: loadedmetadata');
      });
      video.addEventListener('loadstart', () => {
        this.debug('video: loadstart');
      });
      video.addEventListener('stalled', () => {
        this.debug('video: stalled');
      });
    }
  };

  private onMediaSourceOpened = (event: Event): void => {
    // add source buffer
    console.log('mediaSource opened, state: ' + this.rcScreenSession.context.mediaSource.readyState);
    this.sourceBuffer = this.rcScreenSession.context.mediaSource.addSourceBuffer(this.MIME_CODEC);
    //sourceBuffer.mode = 'sequence';

    this.sourceBuffer.addEventListener('updateend', this.onSourceBufferUpdateEnd);
    this.sourceBuffer.addEventListener('error', this.onSourceBufferError);

    this.rcScreenSession.context.videoPlayerReady = true;

    // send the 2nd restart only once to avoid infinite looping
    if (this.rvNeedsResize) {
      this.rvNeedsResize = false;
      // reattempt to resize the media for the RV where the skin could not be loaded
      // The 2nd message with the same scale and quality should be ignored in other cases
      this.remoteControlConnection.applyChange(true);
    }
    this.rcScreenSession.context.networkInfo = {
      bytes: 0,
      frames: 0,
      lastAckTime: Date.now(),
      recentKeyframes: [],
      //pingResponseTime: 0,
    };

    if (this.queue) {
      const buf = this.queue;
      this.queue = null;
      this.updateBuffer(buf, true);
    }
  };

  private onMediaSourceClosed = (event: any): void => {
    console.log('mediaSource closed');
  };

  private onSourceBufferUpdateEnd = (event: Event): void => {
    this.debug(
      'sourceBuffer updateend, state: ' +
        this.rcScreenSession.context.video.readyState +
        ', paused: ' +
        this.rcScreenSession.context.video.paused +
        ', duration: ' +
        this.getAvailableTime()
    );

    // after appending a key frame the player might be paused for a moment, and calling play() in this case might cause problem. move to onactivate() ?
    if (this.rcScreenSession.context.video.paused) {
      // when tab is inactive, the video player might be paused
      if (!document.hidden) {
        this.rcScreenSession.context.video.muted = true;
        this.rcScreenSession.context.video.play();
      } else if (!this._pageVisibleSubscription$) {
        // DOM pauses silent video streaming when the tab is hidden to preserve power. When the unchecked
        // updateend handler keeps restarting it, this results in the DOMException `play() request was interrupted...`
        // We defer these play restarts until the document is visible again, to avoid the DOMException

        // it is not efficient to keep the pageVisible$ firing, as having the page
        // hidden during the RC session is uncommon
        this._pageVisibleSubscription$ = this.visibilityService.pageVisible$
          .pipe(skipWhile(isVisible => !isVisible))
          .subscribe(isVisible => {
            this.unsubscribeToPageVisibile();
            console.log(
              `onSourceBufferUpdateEnd should be called only once, when the page visibility changes to true. isVisible: ${isVisible}`
            );
            if (this.rcScreenSession.context.video.paused) {
              this.rcScreenSession.context.video.muted = true;
              this.rcScreenSession.context.video.play();
            }
          });
      }
    }

    this.clearBuffer();
  };

  private onSourceBufferError = (error: any): void => {
    this.error('sourceBuffer error occurred, state: ' + this.rcScreenSession.context.mediaSource.readyState);
    this.error(JSON.stringify(error));
  };

  private clearBuffer = (): void => {
    if (
      this.sourceBuffer &&
      !this.sourceBuffer.updating &&
      this.keyFrames.length > 0 &&
      this.sourceBuffer.buffered &&
      this.sourceBuffer.buffered.length
    ) {
      // find the last key frame that has been played for more than 5 seconds
      const currentTime = this.rcScreenSession.context.video.currentTime;
      let lastKeyFrame = 0;
      while (this.keyFrames.length > 0) {
        if (this.keyFrames[0] + 5 > currentTime) {
          break;
        }
        lastKeyFrame = this.keyFrames.shift();
      }

      if (lastKeyFrame > 0) {
        lastKeyFrame -= 5; // leave 5 seconds threshold to avoid removing the next key frame
        let from = 0;
        let to = 0;
        for (let i = 0; i < this.sourceBuffer.buffered.length; ++i) {
          const start = this.sourceBuffer.buffered.start(i);
          const end = this.sourceBuffer.buffered.end(i);
          console.log('clearBuffer: buffer start ' + start + ', end ' + end + ', lastKeyFrame = ' + lastKeyFrame);

          if (start < lastKeyFrame) {
            // remove from start to lastKeyFrame, afterwards lastKeyFrame will be start
            from = start;
            to = lastKeyFrame;
          }
        }

        if (from < to) {
          this.info(
            'clearBuffer: remove(' +
              from +
              ', ' +
              to +
              '), keyFrames = ' +
              this.keyFrames.length +
              ', currentTime = ' +
              currentTime
          );
          this.sourceBuffer.remove(from, to);
        }
      }
    }
  };

  public updateBuffer = (buf: Uint8Array, isKeyFrame: boolean): void => {
    const availTime = this.getAvailableTime();
    const currentTime = this.rcScreenSession.context.video.currentTime;
    this.debug(
      'received ' +
        buf.byteLength +
        ' bytes, updating: ' +
        this.sourceBuffer.updating +
        ', state: ' +
        this.rcScreenSession.context.video.readyState +
        ', duration: ' +
        availTime +
        ', currentTime: ' +
        this.rcScreenSession.context.video.currentTime
    );

    if (isKeyFrame && this.isKeyFrame(buf)) {
      if (availTime === 0) {
        this.startTime = Date.now();
      }

      if (this.lastKeyFrame < 0 || availTime - this.lastKeyFrame > 5) {
        this.keyFrames.push(availTime);
        this.info('keyFrames = ' + this.keyFrames.length + ', newKeyFrame = ' + availTime + ', time = ' + currentTime);
      } else {
        this.info('keyFrames = ' + this.keyFrames.length + ', newKeyFrame = ' + availTime + ' ignored');
      }

      this.lastKeyFrame = availTime;
    }

    if (currentTime > availTime + 0.5 || currentTime < availTime - 0.5) {
      this.debug('forward/rewind from ' + currentTime + ' to ' + availTime);
      this.rcScreenSession.context.video.currentTime = availTime;
    }

    // push buffer into queue first, so that if update fails will retry next time
    this.queueBuffer(buf);

    if (!this.sourceBuffer.updating) {
      try {
        this.sourceBuffer.appendBuffer(this.queue);
        this.queue = null;
      } catch (e) {
        this.error('appendBuffer error: ' + JSON.stringify(e));
      }
    }
  };

  public queueBuffer = (buf: Uint8Array): void => {
    if (this.queue == null) {
      this.queue = buf;
    } else {
      const tmp = new Uint8Array(this.queue.byteLength + buf.byteLength);
      tmp.set(this.queue, 0);
      tmp.set(buf, this.queue.byteLength);
      this.queue = tmp;
    }

    this.debug('queued buffer: ' + this.queue.byteLength);
  };

  public sendAck = (): void => {
    const now = Date.now();

    this.remoteControlConnection.send(new ThAckMessage());

    const ackTime = now - this.rcScreenSession.context.networkInfo.lastAckTime;
    const bps = Math.floor((this.rcScreenSession.context.networkInfo.bytes * 8000) / ackTime);
    this.info(
      `ack: ${this.rcScreenSession.context.networkInfo.bytes.toLocaleString()}/${
        this.rcScreenSession.context.networkInfo.frames
      } bytes in ${ackTime.toLocaleString()} ms, ${bps.toLocaleString()} bps, video time: ${
        this.rcScreenSession.context.video.currentTime
      } @ ${this.getAvailableTime()}`
    );

    // check network performance and decrease/increase image quality
    //*
    const keyFrames = this.rcScreenSession.context.networkInfo.recentKeyframes;
    const threshold = Math.max(ackTime, 5000);
    if (
      keyFrames.length >= 4 &&
      now - keyFrames[keyFrames.length - 1] <= threshold &&
      (this.remoteControlConnection.scaleFactor > this.MIN_SCALE_FACTOR ||
        this.remoteControlConnection.getQuality() > this.MIN_QUALITY)
    ) {
      let i = keyFrames.length - 1;
      for (; i > 0; i--) {
        if (keyFrames[i] - keyFrames[i - 1] > threshold) {
          break;
        }
      }

      if (i == 0) {
        const factor = Math.max(this.remoteControlConnection.scaleFactor - 0.1, this.MIN_SCALE_FACTOR);
        const quality = Math.max(this.remoteControlConnection.getQuality() - 10, this.MIN_QUALITY);
        console.log(
          `slow network detected, decrease scale factor ${
            this.remoteControlConnection.scaleFactor
          } -> ${factor}, quality ${this.remoteControlConnection.getQuality()} -> ${quality}`
        );
        this.remoteControlConnection.scaleFactor = factor;
        this.remoteControlConnection.applyChange(false, undefined, quality);
        this.rcScreenSession.context.networkInfo.recentKeyframes = [];
      }
    } else if (
      keyFrames.length >= 4 &&
      now - keyFrames[keyFrames.length - 1] > 10000 &&
      (this.remoteControlConnection.scaleFactor < this.MAX_SCALE_FACTOR ||
        this.remoteControlConnection.getQuality() < this.rcScreenSession.context.imageQuality)
    ) {
      let i = keyFrames.length - 1;
      for (; i > 0; i--) {
        if (keyFrames[i] - keyFrames[i - 1] <= 10000) {
          break;
        }
      }

      if (i == 0) {
        const factor = Math.min(this.remoteControlConnection.scaleFactor + 0.05, this.MAX_SCALE_FACTOR);
        const quality = Math.min(
          this.remoteControlConnection.getQuality() + 5,
          this.rcScreenSession.context.imageQuality
        );
        console.log(
          `fast network detected, increase scale factor ${
            this.remoteControlConnection.scaleFactor
          } -> ${factor}, quality ${this.remoteControlConnection.getQuality()} -> ${quality}`
        );
        this.remoteControlConnection.scaleFactor = factor;
        this.remoteControlConnection.applyChange(false, undefined, quality);
        this.rcScreenSession.context.networkInfo.recentKeyframes = [];
      }
    }
    //*/

    this.rcScreenSession.context.networkInfo.bytes = 0;
    this.rcScreenSession.context.networkInfo.frames = 0;
    this.rcScreenSession.context.networkInfo.lastAckTime = now;
  };

  private getAvailableTime() {
    let availTime = 0;
    if (!!this.sourceBuffer?.buffered?.length) {
      for (let i = 0; i < this.sourceBuffer.buffered.length; ++i) {
        const end = this.sourceBuffer.buffered.end(i);
        if (availTime < end) {
          availTime = end;
        }
      }
    }
    return availTime;
  }

  private findAtom(frames: Uint8Array, start: number, end: number, key: string) {
    while (start < end) {
      const size = ArrayHelper.getInt32(frames, start);
      if (size <= 8) {
        break;
      }

      if (ArrayHelper.startWith(frames, start + 4, key)) {
        return start;
      }

      start += size;
    }
    return -1;
  }

  private isKeyFrame(frames: Uint8Array): boolean {
    // check flags in moof => traf => trun
    let index = 0;
    while (true) {
      const moof = this.findAtom(frames, index, frames.byteLength, 'moof');
      if (moof < 0) {
        break;
      }

      const size = ArrayHelper.getInt32(frames, moof);
      const traf = this.findAtom(frames, moof + 8, moof + size, 'traf');
      if (traf > 0) {
        let trun = this.findAtom(
          frames,
          traf + 8,
          traf + ArrayHelper.getInt32(frames, traf),
          'trun'
        );
        if (trun > 0) {
          trun += 8;
          const numOfFrames = ArrayHelper.getInt32(frames, trun + 4);
          for (let i = 0; i < numOfFrames; i++) {
            const flags = frames[trun + i * 16 + 20];
            if ((flags & 0x02) != 0) {
              return true;
            }
          }
        }
      }

      index = moof + size; // get to the next atom
    }

    return false;
  }

  private debug = (log: string): void => {
    console.debug((Date.now() - this.startTime).toLocaleString() + ': ' + log);
  };

  private info = (log: string): void => {
    console.log((Date.now() - this.startTime).toLocaleString() + ': ' + log);
  };

  private error = (log: string): void => {
    console.error((Date.now() - this.startTime).toLocaleString() + ': ' + log);
  };

  private isAllowedUserInteraction = (): boolean =>
    RcContext.instance.security.RemoteControlInput || this.isAnnotating();

  private isAnnotating = (): boolean => {
    const state = this.annotationService.currentState;
    return state && state.isAnnotating && !state.annotationPaused;
  };
}
